import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSettings, useUpdateAppSettings } from '../../hooks/useAdmin';
import { useReleaseFlagsContext } from '../../services/flagsContext';
import { COVERAGE_HOLIDAY_COUNTRIES } from '../../constants/coverage';
import { RequireFlag } from '../../components/auth/RequireFlag';
import {
  numberFormatter,
  formatNumberInLocale,
  LEGACY_EN_NUMBER_LOCALE,
} from '../../utils/localeFormat';
import {
  DATE_FORMAT_OPTIONS,
  DATE_FORMAT_OPTION_LABELS,
  DEFAULT_DATE_FORMAT_OPTION,
  dateFormatOptionForLocale,
  formatDate,
} from '../../utils/dateFormat';
import {
  INSTANCE_LOCALES,
  FX_RATE_SOURCES,
  fxRateSourceLabel,
  type FxRateSource,
} from '../../services/api/instanceSettings';

/**
 * MKS-314 widened this from the Phase 98 triple to every code a rate source
 * publishes, so a non-CZ instance can pre-fill new projects in its own
 * currency. Hand-synced with `backend/src/services/supportedCurrencies.ts`;
 * the backend PATCH enum is the real gate, this list only shapes the control.
 */
const DEFAULT_CURRENCY_OPTIONS = [
  'AUD', 'BGN', 'BRL', 'CAD', 'CHF', 'CNY', 'CZK', 'DKK', 'EUR', 'GBP', 'HKD',
  'HUF', 'IDR', 'ILS', 'INR', 'ISK', 'JPY', 'KRW', 'MXN', 'MYR', 'NOK', 'NZD',
  'PHP', 'PLN', 'RON', 'SEK', 'SGD', 'THB', 'TRY', 'USD', 'XDR', 'ZAR',
] as const;

/** MKS-314 — the language each locale tag names. */
const LOCALE_LABELS: Record<string, string> = {
  'cs-CZ': 'Czech',
  'en-GB': 'British English',
  'en-US': 'US English',
  'de-DE': 'German',
  'sk-SK': 'Slovak',
};

/**
 * The date every option's sample is shown on: 1 October 2026.
 *
 * Deliberately a day ≤ 12 with a single-digit day AND month, so the sample
 * shows both the field order (`01.10.` vs `10/01/`) and the zero-padding that
 * the staging defect was missing (`1. 10. 2026`).
 */
const LOCALE_SAMPLE_DATE = '2026-10-01';
const LOCALE_SAMPLE_NUMBER = 1234.5;

/**
 * How one locale option reads:
 * `cs-CZ — Czech: European format (default) — 01.10.2026 · 1 234,50`.
 *
 * Both samples are RENDERED, through the same `utils/dateFormat` and
 * `utils/localeFormat` code the app formats with, rather than typed into a
 * literal. A hand-typed sample is a second source of truth, and this control's
 * previous one had already drifted: it promised `20. 11. 2026` for Czech,
 * which is precisely the output this ticket removed.
 */
function localeOptionLabel(code: string): string {
  const option = dateFormatOptionForLocale(code);
  const name = LOCALE_LABELS[code] ?? code;
  const isDefault = option === DEFAULT_DATE_FORMAT_OPTION ? ' (default)' : '';
  const date = formatDate(LOCALE_SAMPLE_DATE, DATE_FORMAT_OPTIONS[option]);
  const number = formatNumberInLocale(LOCALE_SAMPLE_NUMBER, code, {
    minimumFractionDigits: 2,
  });
  const format = `${DATE_FORMAT_OPTION_LABELS[option]} format${isDefault}`;
  return `${code} — ${name}: ${format} — ${date} · ${number}`;
}

/**
 * Whole-koruna formatter for the derived per-month revenue target hint.
 *
 * MKS-314 made this a FUNCTION rather than a module-level `Intl.NumberFormat`:
 * built at import time it would capture whatever locale was in force before
 * the instance-settings query resolved, and then never follow the setting.
 */
const czkWhole = () =>
  numberFormatter(
    { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 },
    LEGACY_EN_NUMBER_LOCALE
  );

const sections: { title: string; description: string; path: string; flag?: string }[] = [
  { title: 'Employee Types', description: 'Configure employee type categories', path: '/admin/employee-types' },
  { title: 'Time Off Types', description: 'Configure time off type categories', path: '/admin/time-off-types' },
  { title: 'Employee Extra Cost Types', description: 'Configure employee extra cost categories', path: '/admin/employee-extra-cost-types' },
  { title: 'Labels', description: 'Manage project labels and categories', path: '/admin/labels' },
  { title: 'Event Types', description: 'Manage team event types', path: '/admin/event-types' },
  { title: 'Web Analytics Sites', description: 'Manage tracked product website hostnames and their canonical GA4 stream', path: '/admin/web-analytics-sites' },
  { title: 'Project Roles', description: 'Manage project assignment roles', path: '/admin/project-roles' },
  { title: 'Role Matrix', description: 'Assign project roles to users', path: '/admin/role-matrix' },
  { title: 'Payment Terms', description: 'Manage invoice payment term options for leads', path: '/admin/payment-terms' },
  { title: 'Pipeline Stages', description: 'Configure the lead board columns, their kind and win probability', path: '/admin/lead-stages' },
  { title: 'Presales Project Mappings', description: 'Map lead labels to a parent project for auto-created presales sub-projects', path: '/admin/label-project-mappings' },
  { title: 'Task Labels', description: 'Manage system-wide task labels shared by every project board', path: '/admin/task-labels' },
];

const retentionFields = [
  { key: 'retention_entries_months', label: 'Time entries', stateKey: 'entries' },
  { key: 'retention_time_offs_months', label: 'Time offs', stateKey: 'timeOffs' },
  { key: 'retention_expenses_months', label: 'Expenses', stateKey: 'expenses' },
  { key: 'retention_assignments_months', label: 'Assignments (deleted)', stateKey: 'assignments' },
  { key: 'retention_invoices_months', label: 'Invoices', stateKey: 'invoices' },
  { key: 'retention_tasks_months', label: 'Tasks', stateKey: 'tasks' },
] as const;

type RetentionState = Record<(typeof retentionFields)[number]['stateKey'], number>;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  // A flagged section's tile is hidden until its flag is on, so the
  // dashboard never links to a page `RequireFlag` would bounce.
  const flags = useReleaseFlagsContext();
  const visibleSections = sections.filter((s) => !s.flag || flags.isOn(s.flag));
  const { data: settings, isLoading: settingsLoading } = useAppSettings();
  const updateSettings = useUpdateAppSettings();

  const [archiveMonths, setArchiveMonths] = useState<number>(3);
  const [auditRetention, setAuditRetention] = useState<number>(24);
  const [leadStaleDays, setLeadStaleDays] = useState<number>(14);
  const [engMetricsWeeks, setEngMetricsWeeks] = useState<number>(13);
  const [ga4PropertyId, setGa4PropertyId] = useState<string>('');
  const [holidayCountryCode, setHolidayCountryCode] = useState<string>('CZ');
  const [defaultCurrency, setDefaultCurrency] = useState<string>('CZK');
  // MKS-314 — the three instance settings. `reportingCurrency` is '' when the
  // key has never been written, which the save path turns back into `null`:
  // "unset" is a real state that falls back to `default_currency`, and is not
  // the same as "explicitly CZK".
  const [instanceLocale, setInstanceLocale] = useState<string>('cs-CZ');
  const [reportingCurrency, setReportingCurrency] = useState<string>('');
  const [fxRateSource, setFxRateSource] = useState<FxRateSource>('ecb');
  const [annualRevenueTarget, setAnnualRevenueTarget] = useState<number>(120_000_000);
  // MKS-214 — company-wide invoice payment term in days.
  const [invoiceDueDays, setInvoiceDueDays] = useState<number>(14);
  const [retention, setRetention] = useState<RetentionState>({
    entries: 24,
    timeOffs: 24,
    expenses: 24,
    assignments: 24,
    invoices: 24,
    tasks: 24,
  });
  const [dirty, setDirty] = useState(false);

  const hasRetentionError = Object.values(retention).some((v) => v < 6 || v > 120);
  const hasAuditRetentionError = auditRetention !== 0 && (auditRetention < 6 || auditRetention > 120);
  const hasEngMetricsWeeksError = engMetricsWeeks < 1 || engMetricsWeeks > 104;
  const hasAnnualRevenueTargetError =
    !Number.isInteger(annualRevenueTarget) ||
    annualRevenueTarget < 0 ||
    annualRevenueTarget > 100_000_000_000;
  // Mirrors the backend bound in api/routes/appSettings.ts; the server check
  // is the real control, this only tells the admin before they press Save.
  const hasInvoiceDueDaysError =
    !Number.isInteger(invoiceDueDays) || invoiceDueDays < 0 || invoiceDueDays > 365;

  useEffect(() => {
    if (settings) {
      setArchiveMonths(settings.auto_archive_inactive_months);
      setAuditRetention(settings.audit_trail_retention_months);
      setLeadStaleDays(settings.lead_staleness_days ?? 14);
      setEngMetricsWeeks(settings.engineering_metrics_initial_weeks ?? 13);
      setGa4PropertyId(settings.ga4_property_id ?? '');
      setHolidayCountryCode(settings.holiday_country_code ?? 'CZ');
      setDefaultCurrency(settings.default_currency ?? 'CZK');
      setInstanceLocale(settings.instance_locale ?? 'cs-CZ');
      setReportingCurrency(settings.reporting_currency ?? '');
      setFxRateSource(settings.fx_rate_source ?? 'ecb');
      setAnnualRevenueTarget(settings.annual_revenue_target_czk ?? 120_000_000);
      setInvoiceDueDays(settings.invoice_default_due_days ?? 14);
      setRetention({
        entries: settings.retention_entries_months,
        timeOffs: settings.retention_time_offs_months,
        expenses: settings.retention_expenses_months,
        assignments: settings.retention_assignments_months,
        invoices: settings.retention_invoices_months,
        tasks: settings.retention_tasks_months ?? 24,
      });
      setDirty(false);
    }
  }, [settings]);

  const handleSaveSettings = () => {
    updateSettings.mutate({
      auto_archive_inactive_months: archiveMonths,
      audit_trail_retention_months: auditRetention,
      lead_staleness_days: leadStaleDays,
      engineering_metrics_initial_weeks: engMetricsWeeks,
      ga4_property_id: ga4PropertyId.trim() ? ga4PropertyId.trim() : null,
      holiday_country_code: holidayCountryCode,
      default_currency: defaultCurrency,
      instance_locale: instanceLocale,
      // '' means "no override" — send null so the handler deletes the row and
      // getReportingCurrency() falls back to default_currency.
      reporting_currency: reportingCurrency === '' ? null : reportingCurrency,
      fx_rate_source: fxRateSource,
      annual_revenue_target_czk: annualRevenueTarget,
      invoice_default_due_days: invoiceDueDays,
      retention_entries_months: retention.entries,
      retention_time_offs_months: retention.timeOffs,
      retention_expenses_months: retention.expenses,
      retention_assignments_months: retention.assignments,
      retention_invoices_months: retention.invoices,
      retention_tasks_months: retention.tasks,
    }, {
      onSuccess: () => {
        setDirty(false);
      },
    });
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900">Administration</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visibleSections.map((section) => (
          <button
            key={section.path}
            onClick={() => navigate(section.path)}
            className="bg-white rounded-lg border border-gray-200 p-6 text-left hover:border-primary-300 hover:shadow-sm transition-all"
          >
            <h3 className="text-lg font-medium text-gray-900">{section.title}</h3>
            <p className="mt-1 text-sm text-gray-500">{section.description}</p>
          </button>
        ))}
      </div>

      <h2 className="text-xl font-semibold text-gray-900">System Settings</h2>

      <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
        <div className="max-w-md">
          <label htmlFor="archiveMonths" className="block text-sm font-medium text-gray-700">
            Auto-archive inactive users (months)
          </label>
          <div className="mt-1">
            <input
              id="archiveMonths"
              type="number"
              min={0}
              max={24}
              value={archiveMonths}
              onChange={(e) => {
                setArchiveMonths(parseInt(e.target.value, 10) || 0);
                setDirty(true);
              }}
              disabled={settingsLoading}
              className="block w-24 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Users who have not reported hours in this period will be archived. Set to 0 to disable.
          </p>
        </div>

        <div className="max-w-md">
          <label htmlFor="auditRetention" className="block text-sm font-medium text-gray-700">
            Audit trail retention (months)
          </label>
          <div className="mt-1">
            <input
              id="auditRetention"
              type="number"
              min={0}
              max={120}
              value={auditRetention}
              onChange={(e) => {
                setAuditRetention(parseInt(e.target.value, 10) || 0);
                setDirty(true);
              }}
              disabled={settingsLoading}
              className={`block w-24 rounded-md shadow-sm sm:text-sm ${
                hasAuditRetentionError
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              }`}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            History/audit trail records older than this will be permanently deleted. Set to 0 to keep forever.
          </p>
          {hasAuditRetentionError && (
            <p className="mt-1 text-xs text-red-600">Must be 0 (disabled) or between 6 and 120</p>
          )}
        </div>

        <div className="max-w-md">
          <label htmlFor="engMetricsWeeks" className="block text-sm font-medium text-gray-700">
            Engineering metrics initial-ingest window (weeks)
          </label>
          <div className="mt-1">
            <input
              id="engMetricsWeeks"
              type="number"
              min={1}
              max={104}
              value={engMetricsWeeks}
              onChange={(e) => {
                setEngMetricsWeeks(parseInt(e.target.value, 10) || 0);
                setDirty(true);
              }}
              disabled={settingsLoading}
              className={`block w-24 rounded-md shadow-sm sm:text-sm ${
                hasEngMetricsWeeksError
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              }`}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            How far back the daily GitHub ingest reaches when a repo has no
            cursor yet (newly discovered, or paused-then-resumed). Normal
            incremental ingests are unaffected. Spec default: 13 weeks
            (~3 months). Admin <em>Backfill</em> can still request a deeper
            window on demand.
          </p>
          {hasEngMetricsWeeksError && (
            <p className="mt-1 text-xs text-red-600">Must be between 1 and 104</p>
          )}
        </div>

        <div className="max-w-md">
          <label htmlFor="leadStaleDays" className="block text-sm font-medium text-gray-700">
            Lead staleness days
          </label>
          <div className="mt-1">
            <input
              id="leadStaleDays"
              type="number"
              min={0}
              max={365}
              value={leadStaleDays}
              onChange={(e) => {
                setLeadStaleDays(parseInt(e.target.value, 10) || 0);
                setDirty(true);
              }}
              disabled={settingsLoading}
              className="block w-24 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Leads not updated within this many days will be marked as stale. Set to 0 to disable.
          </p>
        </div>

        <div className="max-w-md">
          <label htmlFor="annualRevenueTarget" className="block text-sm font-medium text-gray-700">
            Annual revenue target (CZK)
          </label>
          <div className="mt-1">
            <input
              id="annualRevenueTarget"
              type="number"
              min={0}
              max={100000000000}
              step={1000000}
              value={annualRevenueTarget}
              onChange={(e) => {
                setAnnualRevenueTarget(parseInt(e.target.value, 10) || 0);
                setDirty(true);
              }}
              disabled={settingsLoading}
              className={`block w-48 rounded-md shadow-sm sm:text-sm ${
                hasAnnualRevenueTargetError
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              }`}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Invoiced-revenue goal for a full year. The{' '}
            <button
              type="button"
              onClick={() => navigate('/reports/revenue')}
              className="text-primary-600 hover:underline"
            >
              Monthly Revenue
            </button>{' '}
            report divides it by 12 for its per-month target
            {annualRevenueTarget > 0 && (
              <> — currently {czkWhole().format(annualRevenueTarget / 12)} per month</>
            )}
            . Set to 0 to hide the target comparison.
          </p>
          {hasAnnualRevenueTargetError && (
            <p className="mt-1 text-xs text-red-600">
              Must be a whole number between 0 and 100,000,000,000
            </p>
          )}
        </div>

        <div className="max-w-md">
          <label htmlFor="invoiceDueDays" className="block text-sm font-medium text-gray-700">
            Invoice payment term (days)
          </label>
          <div className="mt-1">
            <input
              id="invoiceDueDays"
              type="number"
              min={0}
              max={365}
              step={1}
              value={invoiceDueDays}
              onChange={(e) => {
                setInvoiceDueDays(parseInt(e.target.value, 10) || 0);
                setDirty(true);
              }}
              disabled={settingsLoading}
              className={`block w-48 rounded-md shadow-sm sm:text-sm ${
                hasInvoiceDueDaysError
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                  : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
              }`}
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            A newly issued invoice is due this many days after its issue date. Finance can
            change the due date on any individual invoice. Set to 0 for due on issue.
          </p>
          {hasInvoiceDueDaysError && (
            <p className="mt-1 text-xs text-red-600">
              Must be a whole number between 0 and 365
            </p>
          )}
        </div>

        <div className="max-w-md">
          <label htmlFor="ga4PropertyId" className="block text-sm font-medium text-gray-700">
            GA4 Property ID
          </label>
          <div className="mt-1">
            <input
              id="ga4PropertyId"
              type="text"
              value={ga4PropertyId}
              onChange={(e) => {
                setGa4PropertyId(e.target.value);
                setDirty(true);
              }}
              disabled={settingsLoading}
              placeholder="e.g. 524114652"
              className="block w-64 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Used by the Web Analytics report to query Google Analytics 4. When left
            empty, the report falls back to the <code>GA4_PROPERTY_ID</code> environment
            variable.
          </p>
        </div>

        <div className="max-w-md">
          <label htmlFor="holidayCountryCode" className="block text-sm font-medium text-gray-700">
            Holiday calendar country
          </label>
          <div className="mt-1">
            <select
              id="holidayCountryCode"
              value={holidayCountryCode}
              onChange={(e) => {
                setHolidayCountryCode(e.target.value);
                setDirty(true);
              }}
              disabled={settingsLoading}
              className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              {COVERAGE_HOLIDAY_COUNTRIES.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Selects the public-holiday calendar used for time-off day totals and the holidays
            widget. Vacation-entitlement and capacity-planning calculations deliberately stay on
            the Czech calendar regardless of this setting.
          </p>
        </div>

        <div className="max-w-md">
          <label htmlFor="defaultCurrency" className="block text-sm font-medium text-gray-700">
            Default currency for new projects
          </label>
          <div className="mt-1">
            <select
              id="defaultCurrency"
              value={defaultCurrency}
              onChange={(e) => {
                setDefaultCurrency(e.target.value as 'CZK' | 'EUR' | 'USD');
                setDirty(true);
              }}
              disabled={settingsLoading}
              className="block w-32 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            >
              {DEFAULT_CURRENCY_OPTIONS.map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            The currency new projects default to when none is chosen. It does not change any
            existing project, invoice or timesheet figure — those stay in the currency they were
            recorded in.
          </p>
        </div>

        <RequireFlag flag="mks_314_company_currency_locale">
          <div className="max-w-md">
            <label htmlFor="instanceLocale" className="block text-sm font-medium text-gray-700">
              Formatting locale
            </label>
            <div className="mt-1">
              <select
                id="instanceLocale"
                value={instanceLocale}
                onChange={(e) => {
                  setInstanceLocale(e.target.value);
                  setDirty(true);
                }}
                disabled={settingsLoading}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                {INSTANCE_LOCALES.map((code) => (
                  <option key={code} value={code}>{localeOptionLabel(code)}</option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              How numbers and dates are written throughout the app, including date pickers and
              XLSX exports. This is formatting only — the interface, and with it every month and
              weekday name, stays in English whichever locale you pick.
            </p>
          </div>

          <div className="max-w-md">
            <label htmlFor="reportingCurrency" className="block text-sm font-medium text-gray-700">
              Reporting currency
            </label>
            <div className="mt-1">
              <select
                id="reportingCurrency"
                value={reportingCurrency}
                onChange={(e) => {
                  setReportingCurrency(e.target.value);
                  setDirty(true);
                }}
                disabled={settingsLoading}
                className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                <option value="">Same as the default currency ({defaultCurrency})</option>
                {DEFAULT_CURRENCY_OPTIONS.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              The one currency company-wide totals are shown in. Projects, rates, invoices and
              expenses keep their own currency; only the aggregates are converted, at the
              reference rate of the relevant date. Issued invoices are never re-converted.
            </p>
          </div>

          <div className="max-w-md">
            <label htmlFor="fxRateSource" className="block text-sm font-medium text-gray-700">
              Exchange rate source
            </label>
            <div className="mt-1">
              <select
                id="fxRateSource"
                value={fxRateSource}
                onChange={(e) => {
                  setFxRateSource(e.target.value as FxRateSource);
                  setDirty(true);
                }}
                disabled={settingsLoading}
                className="block w-48 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              >
                {FX_RATE_SOURCES.map((code) => (
                  <option key={code} value={code}>
                    {code === 'ecb'
                      ? `${fxRateSourceLabel(code)} — euro reference rates`
                      : `${fxRateSourceLabel(code)} — Czech National Bank`}
                  </option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Which published reference rate converts figures into the reporting currency.
              Invoice generation always uses the ČNB rate regardless of this setting, because
              Czech accounting requires it for a foreign-currency invoice.
            </p>
          </div>
        </RequireFlag>

        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Data Retention (months)</h3>
          <p className="text-sm text-gray-500 mb-3">
            Records older than the configured period will be permanently deleted. Minimum 6 months.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-lg">
            {retentionFields.map((field) => (
              <div key={field.key}>
                <label htmlFor={field.key} className="block text-sm text-gray-600">
                  {field.label}
                </label>
                <input
                  id={field.key}
                  type="number"
                  min={6}
                  max={120}
                  value={retention[field.stateKey]}
                  onChange={(e) => {
                    setRetention((prev) => ({
                      ...prev,
                      [field.stateKey]: parseInt(e.target.value, 10) || 0,
                    }));
                    setDirty(true);
                  }}
                  disabled={settingsLoading}
                  className={`mt-1 block w-24 rounded-md shadow-sm sm:text-sm ${
                    retention[field.stateKey] < 6 || retention[field.stateKey] > 120
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'
                  }`}
                />
                {(retention[field.stateKey] < 6 || retention[field.stateKey] > 120) && (
                  <p className="mt-1 text-xs text-red-600">Must be between 6 and 120</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div>
          <button
            onClick={handleSaveSettings}
            disabled={!dirty || hasRetentionError || hasAuditRetentionError || hasEngMetricsWeeksError || hasAnnualRevenueTargetError || hasInvoiceDueDaysError || updateSettings.isPending}
            className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateSettings.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
